import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronRight } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-manrope font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">
      {title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-slate-900">Privacy Policy</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-manrope font-bold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-slate-500 text-sm">
            Last updated: July 2, 2025 &nbsp;·&nbsp; Effective date: July 2, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white rounded-xl border border-slate-200 p-8 md:p-12">

          <p className="text-slate-600 leading-relaxed mb-10">
            Vedamrita Ayurvedic Hospital ("<strong>Vedamrita</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") operates the online appointment booking platform at{' '}
            <a href="https://booking.vedamrita.com" className="text-primary hover:underline">booking.vedamrita.com</a>{' '}
            (the "<strong>Service</strong>"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Service.
          </p>

          <Section title="1. Information We Collect">
            <p><strong>Information you provide directly:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Full name, email address, and phone number when you register</li>
              <li>Appointment details including preferred date, time, and department</li>
              <li>Health-related information you choose to share during consultation booking</li>
              <li>Payment information (processed securely through Razorpay — we do not store card details)</li>
            </ul>
            <p className="mt-3"><strong>Information collected automatically:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>IP address, browser type, and device information</li>
              <li>Pages visited and actions taken on the Service</li>
              <li>Session data and cookies necessary for the Service to function</li>
            </ul>
            <p className="mt-3"><strong>Information from third parties:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>If you sign in with Google, we receive your name and email address from Google</li>
              <li>If you connect Google Calendar, we receive permission to create calendar events on your behalf</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-2">
              <li>To create and manage your account on our booking platform</li>
              <li>To schedule, confirm, and manage your appointments with our doctors</li>
              <li>To send appointment confirmations, reminders, and follow-up communications via email</li>
              <li>To create Google Calendar events for your appointments (only if you connect your calendar)</li>
              <li>To facilitate video consultations via Google Meet</li>
              <li>To process payments for paid services</li>
              <li>To improve our Service and understand how it is used</li>
              <li>To comply with legal obligations under applicable Indian law</li>
            </ul>
          </Section>

          <Section title="3. Sharing Your Information">
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Doctors and hospital staff</strong> at Vedamrita who are involved in your care</li>
              <li><strong>Razorpay</strong> — for secure payment processing (governed by Razorpay's privacy policy)</li>
              <li><strong>Google</strong> — for Calendar integration, Google Meet video links, and OAuth sign-in (governed by Google's privacy policy)</li>
              <li><strong>Resend</strong> — for transactional email delivery</li>
              <li><strong>Law enforcement or government authorities</strong> when required by applicable Indian law</li>
            </ul>
          </Section>

          <Section title="4. Data Storage and Security">
            <p>Your data is stored in MongoDB Atlas cloud databases hosted in secure data centres. We implement the following security measures:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>All passwords are hashed using bcrypt — we never store plain-text passwords</li>
              <li>All data in transit is encrypted via HTTPS/TLS</li>
              <li>Authentication uses signed JWT tokens with expiry</li>
              <li>Google OAuth tokens are stored encrypted and scoped to calendar event creation only</li>
              <li>Access to patient data is restricted to authorised hospital staff</li>
            </ul>
            <p className="mt-3">
              While we take reasonable measures to protect your data, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="5. Cookies">
            <p>We use essential cookies and local storage to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Keep you logged in during your session</li>
              <li>Remember your preferences</li>
            </ul>
            <p className="mt-3">We do not use advertising or tracking cookies. You can disable cookies in your browser settings, but this may affect Service functionality.</p>
          </Section>

          <Section title="6. Google Calendar Integration">
            <p>
              If you choose to connect your Google Calendar, we request only the{' '}
              <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">calendar.events</code>{' '}
              scope, which allows us to <strong>create</strong> appointment events on your behalf. We do <strong>not</strong> read, modify, or delete existing events on your calendar. You may disconnect Google Calendar at any time from the Integrations settings page.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>Under the Information Technology Act, 2000 and applicable Indian privacy regulations, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
              <li><strong>Correction</strong> — request correction of inaccurate data</li>
              <li><strong>Deletion</strong> — request deletion of your account and associated data</li>
              <li><strong>Withdrawal of consent</strong> — disconnect third-party integrations (Google Calendar) at any time</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:info@vedamrita.com" className="text-primary hover:underline">info@vedamrita.com</a>.
            </p>
          </Section>

          <Section title="8. Data Retention">
            <p>We retain your personal information for as long as your account is active or as needed to provide the Service. Appointment records may be retained for up to 7 years for medical and legal compliance purposes. You may request deletion of your account at any time.</p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>Our Service is not directed at children under the age of 18. We do not knowingly collect personal information from minors. If a parent or guardian believes their child has provided us with personal data, please contact us and we will delete it promptly.</p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated effective date. Continued use of the Service after changes constitutes acceptance of the revised policy.</p>
          </Section>

          <Section title="11. Contact Us">
            <p>If you have any questions or concerns about this Privacy Policy, please contact us:</p>
            <div className="mt-3 bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
              <p><strong>Vedamrita Ayurvedic Hospital</strong></p>
              <p>Hisar - Tohana Rd, near MRF Agency, Barwala, Haryana 125121</p>
              <p>Email: <a href="mailto:info@vedamrita.com" className="text-primary hover:underline">info@vedamrita.com</a></p>
              <p>Phone: <a href="tel:+918570023400" className="text-primary hover:underline">+91 85700-23400</a></p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
